import {Loader} from '@ambler/andive-next/deprecated'
import Router from 'next/router'
import type {User} from 'oidc-client-ts'
import React from 'react'
import {AuthProvider as OidcAuthProvider, useAuth as useOidcAuth} from 'react-oidc-context'
import {ErrorCard} from '../../components/error'
import Sentry from '../sentry'
import {
  getKeycloakUserManager,
  idpHint,
  isKeycloakConfigured,
  SIGNED_OUT_PATH,
  isRenewalDead,
  onRenewalDeadChange,
  renewKeycloakUser,
} from './keycloak'

export type AuthSession = {
  isAuthenticated: boolean
  isLoading: boolean
  /** The last authentication failure, mostly a callback the library refused. */
  error: Error | undefined
  /** returnTo is an in-app path (router.asPath), never an absolute URL. */
  signIn: (options?: {returnTo?: string}) => void
  signOut: () => Promise<void> | void
}

// No default value: a consumer mounted outside the provider must fail loudly
// rather than sit on an inert session that never signs in.
const AuthSessionContext = React.createContext<AuthSession | undefined>(undefined)

export const useAuthSession = (): AuthSession => {
  const session = React.useContext(AuthSessionContext)
  if (!session) {
    throw new Error('useAuthSession must be used within an AuthSessionProvider')
  }

  return session
}

/**
 * Keeps only a same-app path worth restoring after a login. Rejects protocol-
 * relative URLs (`//evil.example` starts with `/` yet is not in-app), and
 * login-callback URLs — restoring a consumed `?code&state` would replay the
 * callback and redirect-bounce on every reload, forever.
 */
const sanitizeReturnTo = (returnTo?: string): string | undefined => {
  if (!returnTo || returnTo === '/') {
    return undefined
  }
  if (!returnTo.startsWith('/') || returnTo.startsWith('//')) {
    return undefined
  }
  const query = returnTo.split('?')[1]
  if (query) {
    const params = new URLSearchParams(query)
    if (params.has('code') && params.has('state')) {
      return undefined
    }
  }

  return returnTo
}

const SessionBridge: React.FC = ({children}) => {
  const {isAuthenticated, isLoading, error, signoutRedirect} = useOidcAuth()

  // A renewal that failed for good must surface as signed-out: the library
  // only records the error and would otherwise keep reporting an authenticated
  // user with no usable token, forever. The state lives in the module, set
  // from the manager's own events, so a failure that lands before this
  // component mounts is not missed.
  const [renewalDead, setRenewalDead] = React.useState(isRenewalDead)
  React.useEffect(() => onRenewalDeadChange(setRenewalDead), [])

  // Nothing else reports these: _app only reads `router.query.error`, which a
  // failed callback never carries.
  React.useEffect(() => {
    if (error) {
      Sentry.captureException(error)
    }
  }, [error])

  // One redirect at a time: the sign-in effect in _app re-fires on every auth
  // state change and would otherwise stack redirects, or loop on a failure.
  const redirectStartedRef = React.useRef(false)

  const value = React.useMemo<AuthSession>(
    () => ({
      isAuthenticated: isAuthenticated && !renewalDead,
      isLoading,
      error,
      signIn: ({returnTo} = {}) => {
        if (redirectStartedRef.current) {
          return
        }
        redirectStartedRef.current = true
        void (async () => {
          try {
            // In-app refresh first: a stored user usually still holds a valid
            // refresh token, and the full redirect bounce through Keycloak is
            // only needed when that fails.
            const renewed = await renewKeycloakUser().catch(() => null)
            if (renewed) {
              return
            }
            // The UserManager directly, not the context's signinRedirect: the
            // wrapper swallows rejections, and its dispatches re-render the
            // tree — which is what turned a failure into a retry loop.
            await getKeycloakUserManager().signinRedirect({
              state: {returnTo: sanitizeReturnTo(returnTo)},
              // Straight to the provider the realm federates the staff through,
              // skipping the identity-first page organizations install by
              // default. Safe because the hint and the sign-in screen come
              // together: where a provider is named, the sign-in only ever
              // starts from a click, so nothing replays silently after a
              // sign-out.
              ...(idpHint ? {extraQueryParams: {kc_idp_hint: idpHint}} : {}),
            })
          } catch (_err) {
            // Keycloak unreachable: surface it — without this the sign-in
            // effect hammers the discovery endpoint in a tight loop.
            void Router.replace('/auth/error?error=signin_failed')
          } finally {
            redirectStartedRef.current = false
          }
        })()
      },
      signOut: () => signoutRedirect({post_logout_redirect_uri: `${window.location.origin}${SIGNED_OUT_PATH}`}),
    }),
    [isAuthenticated, isLoading, error, renewalDead, signoutRedirect],
  )

  return <AuthSessionContext.Provider value={value}>{children}</AuthSessionContext.Provider>
}

const onSigninCallback = (user: User | void): void => {
  const returnTo = user ? sanitizeReturnTo((user.state as {returnTo?: string} | undefined)?.returnTo) : undefined
  // Router.replace, never a bare history.replaceState: it strips the consumed
  // ?code&state AND rewrites the entry with Next's own history state — an
  // entry without it is ignored on popstate (dead back button) and leaves a
  // stale router.asPath.
  void Router.replace(returnTo ?? window.location.pathname)
}

export const AuthSessionProvider: React.FC = ({children}) => {
  // Mount first: the UserManager reads window (redirect uri, stores), so it
  // cannot be built during the server render — and the server markup must
  // match the first client pass, since React 17 patches a mismatched tree
  // instead of replacing it.
  const [mounted, setMounted] = React.useState(false)
  React.useEffect(() => {
    setMounted(true)
  }, [])
  if (!mounted) {
    return <Loader />
  }

  // Said out loud rather than degraded into a redirect loop against
  // `undefined/protocol/openid-connect/auth`: a deployment without these
  // variables has no identity provider at all.
  if (!isKeycloakConfigured) {
    return (
      <ErrorCard
        error={{
          message:
            "L'authentification n'est pas configurée sur ce déploiement (KEYCLOAK_ISSUER_URL, KEYCLOAK_CLIENT_ID).",
        }}
      />
    )
  }

  return (
    <OidcAuthProvider userManager={getKeycloakUserManager()} onSigninCallback={onSigninCallback}>
      <SessionBridge>{children}</SessionBridge>
    </OidcAuthProvider>
  )
}
